/*
 * Copyright Camunda Services GmbH and/or licensed to Camunda Services GmbH under
 * one or more contributor license agreements. See the NOTICE file distributed
 * with this work for additional information regarding copyright ownership.
 * Licensed under the Camunda License 1.0. You may not use this file
 * except in compliance with the Camunda License 1.0.
 */

import { useState } from 'react';
import PropTypes from 'prop-types';
import { Close, Renew, Upgrade } from '@carbon/icons-react';
import { Button as CarbonButton } from '@carbon/react';

import { organizationStore } from 'stores';
import { trackingService } from 'services';
import config from 'utils/config';

import * as Styled from './TopBanner.styled';

export default function TopBanner({ open = false, onClose, children, isTrialBanner, dataTest = 'banner', ...props }) {
  if (!open) {
    return null;
  }

  return (
    <Styled.Wrapper role="banner" data-test={dataTest} $isTrialBanner={isTrialBanner} {...props}>
      {children}

      {onClose && (
        <CarbonButton
          data-test="close-top-banner"
          hasIconOnly
          size="sm"
          iconDescription="Dismiss"
          tooltipPosition="bottom"
          tooltipAlignment="end"
          kind="ghost"
          renderIcon={Close}
          onClick={onClose}
        />
      )}
    </Styled.Wrapper>
  );
}

const TrialBanner = (props) => {
  const [showBanner, setShowBanner] = useState(true);

  const cta = organizationStore.hasBillingPermission ? (
    <Styled.Link
      href={`https://console.${config.camundaCloudBaseDomain}/org/${organizationStore.currentOrganization.id}/checkout`}
      target="_blank"
      rel="noopener"
    >
      <CarbonButton
        data-test="upgrade-button"
        size="sm"
        renderIcon={Upgrade}
        kind="tertiary"
        onClick={() => {
          const displayedSalesPlan = organizationStore.currentOrganizationInfo?.salesPlan?.type;
          trackingService.trackCheckoutPage(displayedSalesPlan);
        }}
      >
        Upgrade now
      </CarbonButton>
    </Styled.Link>
  ) : undefined;

  return (
    <TopBanner {...props} isTrialBanner open={props.open && showBanner}>
      <Styled.Message>{`Modeling is free, but your free process execution trial ends in ${organizationStore.remainingTrialDays}`}</Styled.Message>
      {cta}

      <CarbonButton
        data-test="close-top-banner"
        hasIconOnly
        size="sm"
        iconDescription="Dismiss"
        tooltipPosition="bottom"
        tooltipAlignment="end"
        kind="ghost"
        renderIcon={Close}
        onClick={() => setShowBanner(false)}
      />
    </TopBanner>
  );
};

const OutdatedBrowserMessage = (props) => (
  <TopBanner variant="warning" {...props}>
    <strong>Your browser is outdated.</strong>&nbsp;For the best experience, keep your browser up to date. Or
    consider&nbsp;{' '}
    <a rel="noopener noreferrer" href="http://outdatedbrowser.com/en" target="_blank">
      choosing a different browser
    </a>
    , they are pretty awesome.
  </TopBanner>
);

const DownTimeBanner = (props) => (
  <TopBanner variant="info" {...props}>
    <strong>Maintenance Downtime:</strong>&nbsp;Modeler will be unavailable between&nbsp;{' '}
    <strong>
      {props.schedule.startDate} - {props.schedule.startTime}
    </strong>{' '}
    &nbsp;and&nbsp;{' '}
    <strong>
      {props.schedule.endDate} - {props.schedule.endTime}
    </strong>
    .&nbsp;We apologize for any inconvenience.
  </TopBanner>
);

const UpdatedAppMessage = (props) => (
  <TopBanner variant="info" {...props}>
    <strong>Good News!</strong>&nbsp;A new Modeler experience is ready&nbsp;&nbsp;
    <CarbonButton kind="tertiary" size="sm" renderIcon={Renew} onClick={() => window.location.reload()}>
      Refresh now
    </CarbonButton>
  </TopBanner>
);

TopBanner.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  dataTest: PropTypes.string
};

TopBanner.OutdatedBrowserMessage = OutdatedBrowserMessage;
TopBanner.UpdatedAppMessage = UpdatedAppMessage;
TopBanner.DownTimeBanner = DownTimeBanner;
TopBanner.TrialBanner = TrialBanner;
